import { List } from "lucide-react-native";
import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Animated,
  Modal,
  NativeScrollEvent,
  NativeSyntheticEvent,
  ScrollView,
  useWindowDimensions,
} from "react-native";

import {
  QuestionHeader,
  QuestionResults,
  QuestionResultsSkeleton,
} from "@/components/questions";
import { DemographicFilterPanel, FilterButton } from "@/components/runs";
import { SectionHeader, SectionResults } from "@/components/sections/results";
import { StudyMenu, StudyMenuSkeleton } from "@/components/studies";
import {
  Box,
  Card,
  DropdownPicker,
  ErrorWithRetry,
  HStack,
  Pressable,
  Skeleton,
  Text,
  VStack,
} from "@/components/ui";
import { useLocalization, useRunContext, useStudyContext } from "@/hooks";
import API from "@/hooks/api";
import { DemographicFiltersMap, Question, Section } from "@/types/resources";

/* ── Constants ─────────────────────────────────────────── */

const MENU_WIDTH = 320;

/* ── Types ─────────────────────────────────────────────── */

interface QuestionGroup {
  data: Question[];
  section: Section | null;
}

/* A "grouped" section is collapsed into a single nav entry and rendered via
   the composite SectionResults: either a use-case section (report + optional
   detailed toggle) or a config-only section (always-detailed per-question). */
const isGroupedSection = (section: Section | null): boolean =>
  section != null && (section.use_case != null || section.config != null);

interface RunResultsProps {
  disableFiltering?: boolean;
  onFiltersChange?: (numFilters: number) => void;
  onScroll?: (event: NativeSyntheticEvent<NativeScrollEvent>) => void;
}

/* ── Skeleton ───────────────────────────────────────────── */

export const RunResultsSkeleton: FC<{ hideMenu?: boolean }> = ({
  hideMenu = false,
}) => {
  const { width: windowWidth } = useWindowDimensions();
  const isLargeScreen = windowWidth >= 1024;

  return (
    <HStack className="flex-1 min-h-0 gap-4">
      {/* Left column: header card + body, mirroring the loaded layout */}
      <VStack className="flex-1 min-h-0">
        {/* Header card skeleton — mirrors the real two-row header */}
        <Card className="border border-outline-200 rounded-2xl p-5 mb-4">
          <VStack className="gap-3">
            {/* Row 1: controls — dropdown left, filter (+ menu on small) right */}
            <HStack className="items-center gap-2">
              <Skeleton className="h-9 flex-1 lg:flex-none lg:w-56 rounded-xl" />
              <HStack className="items-center gap-2 shrink-0 ml-auto">
                <Skeleton className="h-9 w-16 rounded-lg" />
                {!hideMenu && !isLargeScreen && (
                  <Skeleton className="h-9 w-10 rounded-lg" />
                )}
              </HStack>
            </HStack>
            {/* Row 2: active section / question */}
            <VStack className="gap-2">
              <Skeleton className="h-3 w-24 rounded" />
              <Skeleton className="h-5 w-3/4 rounded" />
            </VStack>
          </VStack>
        </Card>
        {/* Body skeleton */}
        <Box className="flex-1 min-h-0">
          <QuestionResultsSkeleton />
        </Box>
      </VStack>

      {/* Persistent sidebar skeleton on large screens */}
      {!hideMenu && isLargeScreen && <StudyMenuSkeleton />}
    </HStack>
  );
};

/* ── Component ──────────────────────────────────────────── */

const RunResults: FC<RunResultsProps> = ({
  disableFiltering = false,
  onFiltersChange,
  onScroll,
}) => {
  const { i18n } = useLocalization();
  const { runId } = useRunContext();
  const { studyId } = useStudyContext();

  const [params, setParams] = useState({
    filters: {} as DemographicFiltersMap,
    itemsPerPage: 10,
    page: 1,
  });
  const [filterPanelOpen, setFilterPanelOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuRendered, setMenuRendered] = useState(false);
  const { width: windowWidth } = useWindowDimensions();
  const menuWidth = Math.min(MENU_WIDTH, windowWidth * 0.85);
  // On large screens the navigation menu sits as a persistent sidebar; on
  // smaller screens it collapses into the hamburger-triggered overlay.
  const isLargeScreen = windowWidth >= 1024;
  const [menuSlide] = useState(() => new Animated.Value(MENU_WIDTH));
  const [menuBackdrop] = useState(() => new Animated.Value(0));

  /* Collapsing header (Facebook-style): the header card floats above the
     scrolling body. Scrolling *down* slides it up 1:1 with the scroll offset
     until hidden; scrolling *up* (or reaching the top) snaps it fully back
     into view. The body is padded beneath the header so once it's gone the
     content occupies the full height. */
  const [headerHeight, setHeaderHeight] = useState(0);
  const [headerTranslate] = useState(() => new Animated.Value(0));
  const headerHeightRef = useRef(0);
  const lastScrollY = useRef(0);
  const currentTranslate = useRef(0);
  const revealingRef = useRef(false);
  const upDistance = useRef(0);

  const handleScroll = useCallback(
    (event: NativeSyntheticEvent<NativeScrollEvent>) => {
      const y = event.nativeEvent.contentOffset.y;
      const delta = y - lastScrollY.current;
      lastScrollY.current = y;
      const h = headerHeightRef.current || 1;
      // Ignore tiny upward jitters — only reveal after a deliberate scroll up.
      const UP_THRESHOLD = 60;

      if (y <= 0) {
        upDistance.current = 0;
      } else if (delta > 0) {
        // Scrolling down: slide the header up 1:1 with the scroll.
        upDistance.current = 0;
        if (revealingRef.current) {
          headerTranslate.stopAnimation();
          revealingRef.current = false;
        }
        const next = Math.max(-h, currentTranslate.current - delta);
        if (next !== currentTranslate.current) {
          currentTranslate.current = next;
          headerTranslate.setValue(next);
        }
      } else if (delta < 0) {
        // Scrolling up: only reveal once the accumulated upward distance
        // crosses the threshold, so a tiny nudge doesn't pop the header.
        upDistance.current -= delta;
      }

      if (
        (y <= 0 || upDistance.current >= UP_THRESHOLD) &&
        currentTranslate.current !== 0 &&
        !revealingRef.current
      ) {
        revealingRef.current = true;
        currentTranslate.current = 0;
        Animated.timing(headerTranslate, {
          duration: 200,
          toValue: 0,
          useNativeDriver: false,
        }).start(() => {
          revealingRef.current = false;
        });
      }

      onScroll?.(event);
    },
    [headerTranslate, onScroll]
  );
  /* The active menu selection: either a question id, or `section-<id>`
     for a grouped section — use-case or config-only — which renders the
     composite SectionResults instead of an individual question. */
  const [activeId, setActiveId] = useState<string | null>(null);
  const [selectedGroup, setSelectedGroup] = useState<string>("all");

  const {
    count,
    error,
    loading: loadingResults,
    refresh,
    results,
    study,
  } = API.Studies.useGetRunResults(studyId!, runId!, params);

  // A study-level use case means the whole study is a single use-case
  // section, so the navigation menu is pointless — hide it and let the
  // results take the full width.
  const hideMenu = study?.use_case != null;

  const { fetchSections } = API.Studies.useGetSections(studyId!);
  const [sections, setSections] = useState<Section[]>([]);
  const [sectionsFetched, setSectionsFetched] = useState(false);

  useEffect(() => {
    if (studyId) {
      void fetchSections().then((data) => {
        if (data) setSections(data);
        setSectionsFetched(true);
      });
    }
  }, [fetchSections, studyId]);

  /* Auto-load every page so the navigation menu lists everything and any
     entry can be selected without waiting on a per-question fetch. Done during
     render (not in an effect); keying on the loaded length ensures we request
     each next page exactly once as results arrive. */
  const [lastAutoLoadedLength, setLastAutoLoadedLength] = useState(-1);
  if (
    !loadingResults &&
    count > 0 &&
    results.length < count &&
    results.length !== lastAutoLoadedLength
  ) {
    setLastAutoLoadedLength(results.length);
    setParams((prev) => ({ ...prev, page: prev.page + 1 }));
  }

  const sectionMap = useMemo(() => {
    const map = new Map<string, Section>();
    sections.forEach((s) => map.set(s.id, s));
    return map;
  }, [sections]);

  const sortedResults = useMemo(
    () =>
      [...results]
        .filter((r) => r.question.type !== "category_assignment")
        .sort((a, b) => {
          const orderA = "order" in a.question ? a.question.order : 0;
          const orderB = "order" in b.question ? b.question.order : 0;
          return orderA - orderB;
        }),
    [results]
  );

  /* Menu grouping: consecutive questions sharing a section sit under a
     single section header. Grouped sections (use-case or config-only) are
     navigated as a single unit, so their individual questions are hidden
     from the menu (the section entry renders the composite SectionResults). */
  const groupedData = useMemo<QuestionGroup[]>(() => {
    const groups: QuestionGroup[] = [];
    let current: QuestionGroup | null = null;
    sortedResults.forEach((r) => {
      const question = r.question as Question;
      const section = question.section
        ? (sectionMap.get(question.section) ?? null)
        : null;
      if (!current || (current.section?.id ?? null) !== (section?.id ?? null)) {
        current = { data: [question], section };
        groups.push(current);
      } else {
        current.data.push(question);
      }
    });
    return groups.map((g) =>
      isGroupedSection(g.section) ? { ...g, data: [] } : g
    );
  }, [sortedResults, sectionMap]);

  const isSectionActive = activeId?.startsWith("section-") ?? false;
  const activeSectionId = isSectionActive
    ? activeId!.slice("section-".length)
    : null;
  const activeGroupedSection = activeSectionId
    ? (sectionMap.get(activeSectionId) ?? null)
    : null;

  const sectionResults = useMemo(
    () =>
      activeSectionId
        ? sortedResults.filter(
            (r) => (r.question as Question).section === activeSectionId
          )
        : [],
    [sortedResults, activeSectionId]
  );

  const activeResult = useMemo(
    () =>
      !isSectionActive && activeId
        ? (sortedResults.find((r) => r.question.id === activeId) ?? null)
        : null,
    [sortedResults, activeId, isSectionActive]
  );

  const activeQuestion = activeResult?.question as Question | undefined;
  const activeSection = activeQuestion?.section
    ? (sectionMap.get(activeQuestion.section) ?? null)
    : null;

  // Default to the first menu entry once results + sections are ready. Done
  // during render (not in an effect); the `!activeId` guard makes it converge.
  if (!activeId && sectionsFetched && groupedData.length > 0) {
    const first = groupedData[0];
    if (isGroupedSection(first.section)) {
      setActiveId(`section-${first.section!.id}`);
    } else if (first.data[0]) {
      setActiveId(first.data[0].id);
    }
  }

  /* Group dropdown options derived from the active selection's groups (the
     backend guarantees every question in a study shares the same groups).
     An "all" sentinel sits at the head; with a single group there's
     nothing to filter, so the dropdown is hidden. */
  const groupOptions = useMemo(() => {
    const groupResults = (activeResult ?? sectionResults[0])?.groups ?? [];
    const base = groupResults.map((gr, idx) => ({
      label: (gr.group.name ||
        gr.group.panel?.name ||
        i18n.t("studies.group", { index: idx + 1 })) as string,
      value: gr.group.id || String(idx),
    }));
    return base.length > 1
      ? [
          { label: i18n.t("dashboardNavigator.allGroups"), value: "all" },
          ...base,
        ]
      : [];
  }, [activeResult, sectionResults, i18n]);

  const effectiveGroupSelection =
    groupOptions.length > 1 ? selectedGroup : undefined;

  // Pre-narrow the section's results by the selected group before handing
  // them to SectionResults (mirrors the per-question dropdown semantics).
  const narrowedSectionData = useMemo(() => {
    if (!effectiveGroupSelection || effectiveGroupSelection === "all") {
      return sectionResults;
    }
    return sectionResults.map((r) => ({
      ...r,
      groups: r.groups.filter(
        (gr, idx) => (gr.group.id || String(idx)) === effectiveGroupSelection
      ),
    }));
  }, [sectionResults, effectiveGroupSelection]);

  const handleMenuPress = useCallback(
    (id: string) => {
      if (id.startsWith("section-")) {
        const sectionId = id.slice("section-".length);
        const section = sectionMap.get(sectionId) ?? null;
        if (isGroupedSection(section)) {
          setActiveId(id);
        } else {
          const first = sortedResults.find(
            (r) => (r.question as Question).section === sectionId
          );
          if (first) setActiveId(first.question.id);
        }
      } else {
        setActiveId(id);
      }
    },
    [sectionMap, sortedResults]
  );

  const handleFiltersChange = useCallback(
    (newFilters: DemographicFiltersMap) => {
      setParams((prev) => ({ ...prev, filters: newFilters, page: 1 }));
      onFiltersChange?.(Object.keys(newFilters).length);
    },
    [onFiltersChange]
  );

  // Toggle the overlay menu. On web the trigger keeps DOM focus, so when the
  // Modal marks the background `aria-hidden` the focused button would sit inside
  // the hidden subtree (an accessibility violation). Blur it before opening.
  const toggleMenu = useCallback(() => {
    if (typeof document !== "undefined") {
      (document.activeElement as HTMLElement | null)?.blur();
    }
    setMenuOpen((v) => !v);
  }, []);

  // Mount the overlay during render (not in an effect) as soon as it opens; the
  // animation effect below unmounts it once the close animation finishes.
  const [prevMenuOpen, setPrevMenuOpen] = useState(menuOpen);
  if (prevMenuOpen !== menuOpen) {
    setPrevMenuOpen(menuOpen);
    if (menuOpen) setMenuRendered(true);
  }

  /* Slide the navigation menu in/out as a full-height overlay. Keep it
     mounted until the close animation finishes. */
  useEffect(() => {
    Animated.parallel([
      Animated.timing(menuSlide, {
        duration: menuOpen ? 220 : 200,
        toValue: menuOpen ? 0 : menuWidth,
        useNativeDriver: true,
      }),
      Animated.timing(menuBackdrop, {
        duration: menuOpen ? 220 : 200,
        toValue: menuOpen ? 1 : 0,
        useNativeDriver: true,
      }),
    ]).start(() => {
      if (!menuOpen) setMenuRendered(false);
    });
  }, [menuBackdrop, menuOpen, menuSlide, menuWidth]);

  if ((loadingResults && results.length === 0) || !sectionsFetched) {
    return <RunResultsSkeleton hideMenu={hideMenu} />;
  }

  if (error && results.length === 0) {
    return (
      <ErrorWithRetry
        error={error}
        onRetry={refresh}
        title={i18n.t("studies.errorGettingResults")}
      />
    );
  }

  const showSectionResults = isSectionActive && activeGroupedSection != null;

  /* The active panel: header card + scrolling results body, plus the sidebar
     on large screens. When a grouped section (use-case or config-only) is
     active we wrap the whole thing in a `<SectionResults>` compound so its
     `Controls` (rendered as a second row of the header card) and `Body`
     (inside the ScrollView) share state. */
  const panel = (
    <HStack className="flex-1 min-h-0 gap-4">
      {/* `overflow-hidden` clips the floating header as it slides up so it
          never overlaps the content above this panel. */}
      <VStack className="flex-1 min-h-0 overflow-hidden relative">
        {/* Results body — QuestionResults / SectionResults own their cards.
            Padded at the top by the header's height so its content starts
            below the floating header; when the header slides away the
            content scrolls up to fill the whole area. */}
        <ScrollView
          className="flex-1"
          contentContainerStyle={{
            paddingBottom: 20,
            paddingTop: headerHeight + 16,
          }}
          onScroll={handleScroll}
          scrollEventThrottle={16}
          showsVerticalScrollIndicator={false}
        >
          {showSectionResults ? (
            <SectionResults.Body />
          ) : activeResult && activeQuestion ? (
            <QuestionResults
              filters={params.filters}
              questionId={activeQuestion.id}
              result={activeResult}
              runId={runId}
              selectedGroupIndex={effectiveGroupSelection}
            />
          ) : null}
        </ScrollView>

        {/* Header card floats above the body and slides up/down with scroll.
              - Row 1: section/question header (title + media) on the left,
                run-level controls (group dropdown, filter, small-screen
                menu toggle) on the right.
              - Row 2: `SectionResults.Controls` — cell tabs, pager, and
                report ↔ detailed toggle. Only rendered for grouped
                sections (use-case or config-only); regular question
                navigation shows Row 1 only. */}
        <Animated.View
          onLayout={(e) => {
            const h = e.nativeEvent.layout.height;
            headerHeightRef.current = h;
            setHeaderHeight(h);
          }}
          style={{
            left: 0,
            position: "absolute",
            right: 0,
            top: 0,
            transform: [{ translateY: headerTranslate }],
            zIndex: 10,
          }}
        >
          {/* Opaque backdrop (page background, no top radius) so results
            scrolling underneath the rounded header card are hidden. */}
          <Box className="bg-[hsl(var(--background))]">
            <Card className="border border-outline-200 rounded-2xl p-5">
              <VStack className="gap-3">
                {/* Row 1 (desktop): header left, run controls right. On small
                screens we flip to `flex-col-reverse` so the controls sit
                on top and the header takes its own row underneath —
                otherwise the dropdown + filter would crowd next to a wrapped
                title. */}
                <Box className="gap-3 flex-col-reverse md:flex-row md:items-start">
                  <Box className="md:flex-1 md:min-w-0">
                    {showSectionResults ? (
                      <SectionHeader section={activeGroupedSection} />
                    ) : activeQuestion ? (
                      <QuestionHeader
                        question={activeQuestion}
                        sectionTitle={activeSection?.title}
                      />
                    ) : null}
                  </Box>

                  <HStack className="items-center gap-2 md:shrink-0">
                    {groupOptions.length > 1 && (
                      <DropdownPicker
                        className="flex-1 md:flex-none md:w-56"
                        items={groupOptions}
                        onChange={setSelectedGroup}
                        placeholder={i18n.t("dashboardNavigator.allGroups")}
                        size="sm"
                        value={selectedGroup}
                      />
                    )}
                    {(!disableFiltering || (!hideMenu && !isLargeScreen)) && (
                      <HStack className="items-center gap-2 shrink-0 ml-auto">
                        {!disableFiltering && (
                          <FilterButton
                            numFilters={Object.keys(params.filters).length}
                            onPress={() => setFilterPanelOpen(true)}
                          />
                        )}
                        {!hideMenu && !isLargeScreen && (
                          <Pressable
                            className="flex-row items-center justify-center gap-1.5 rounded-lg border border-outline-300 px-3 h-9"
                            onPress={toggleMenu}
                          >
                            <List
                              color="rgb(var(--color-typography-500))"
                              size={16}
                            />
                            <Text className="hidden lg:flex text-sm font-medium text-typography-600">
                              {i18n.t("dashboardNavigator.questions")}
                            </Text>
                          </Pressable>
                        )}
                      </HStack>
                    )}
                  </HStack>
                </Box>

                {showSectionResults && (
                  <>
                    <SectionResults.Controls />
                    {/* Only renders when Controls' inline slot is taken by the
                    cell tab bar; otherwise Controls hosts the QuestionHeader
                    itself so we don't stack two near-empty rows. */}
                    <SectionResults.QuestionHeader />
                  </>
                )}
              </VStack>
            </Card>
          </Box>
        </Animated.View>
      </VStack>

      {/* Persistent navigation sidebar on large screens. Hidden when the
          whole study is a single use-case section. */}
      {!hideMenu && isLargeScreen && (
        <StudyMenu
          activeSectionId={activeId ?? ""}
          groupedData={groupedData}
          loading={loadingResults}
          onItemPress={handleMenuPress}
        />
      )}
    </HStack>
  );

  return (
    <VStack className="flex-1 min-h-0">
      {showSectionResults ? (
        <SectionResults
          data={narrowedSectionData}
          filters={params.filters}
          runId={runId}
          section={activeGroupedSection}
        >
          {panel}
        </SectionResults>
      ) : (
        panel
      )}

      {/* Full-height slide-in navigation menu for small screens. Hidden when
          the whole study is a single use-case section. */}
      {!hideMenu && !isLargeScreen && (
        <Modal
          animationType="none"
          onRequestClose={() => setMenuOpen(false)}
          transparent
          visible={menuRendered}
        >
          <Animated.View
            style={{
              backgroundColor: "rgba(0,0,0,0.4)",
              bottom: 0,
              left: 0,
              opacity: menuBackdrop,
              position: "absolute",
              right: 0,
              top: 0,
            }}
          >
            <Pressable onPress={() => setMenuOpen(false)} style={{ flex: 1 }} />
          </Animated.View>

          <Animated.View
            style={{
              bottom: 0,
              position: "absolute",
              right: 0,
              top: 0,
              transform: [{ translateX: menuSlide }],
              width: menuWidth,
            }}
          >
            <StudyMenu
              activeSectionId={activeId ?? ""}
              groupedData={groupedData}
              loading={loadingResults}
              onClose={() => setMenuOpen(false)}
              onItemPress={(id) => {
                handleMenuPress(id);
                setMenuOpen(false);
              }}
              overlay
            />
          </Animated.View>
        </Modal>
      )}

      {!disableFiltering && (
        <DemographicFilterPanel
          filters={params.filters}
          isOpen={filterPanelOpen}
          onClose={() => setFilterPanelOpen(false)}
          onFiltersChange={handleFiltersChange}
          resourceId={studyId!}
          resourceType="study"
          runId={runId!}
        />
      )}
    </VStack>
  );
};

RunResults.displayName = "RunResults";

export default RunResults;
